import { useMemo, useState, forwardRef, useCallback, useEffect, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { alignItems, strCode } from "../../../../../../consts";
import { deviceModels } from "../../../../consts/deviceModels";
import { audienceSetOptions } from "../../consts/audienceSetOptions";
import { log, notificationService, t } from "../../../../../../services";
import { dropdownUtil, adminUserUtils } from "../../../../../../utils";

import {
  DropdownInput,
  FlexContainers,
  RadioGroupSections,
  WrapperContainer
} from "../../../../../../components";
import SelectedItemsList from "../../../../../../components/DropdownMultiselectInput/SelectedItemsList";
import { CurrentFirmwareVersionInput } from "../../../../components/CurrentFirmwareVersionInput/CurrentFirmwareVersionInput";
import { SectionTitle, SectionWrapper } from "../../styled";

const allMarketsOption = { id: 0, value: strCode.SELECT_ALL_TITLE };

const AudienceSection = forwardRef(({ audienceData }, ref) => {
  const markets = useSelector((state) => state.markets?.markets);
  const apps = useSelector((state) => state.configs.appTypesGroups);
  const devices = useSelector((state) => state.fleetManagement.devices);
  const users = useSelector((store) => store.users?.users);

  const [audienceSetOption, setAudienceSetOption] = useState(
    audienceData?.audienceSetOption ?? audienceSetOptions.APP
  );
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedDusns, setSelectedDusns] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [versions, setVersions] = useState([]);

  const [isValid, setIsValid] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState(null);

  const deviceOptions = useMemo(() => {
    return Object.keys(deviceModels)?.map((device) => {
      return { id: device, value: device };
    });
  }, []);

  const dusnOptions = useMemo(() => {
    return devices?.map((device) => {
      return { id: device?.dusn, value: device?.dusn };
    });
  }, [devices]);

  const userOptions = useMemo(() => {
    return users
      ?.filter((user) => !adminUserUtils.isUserAdmin(user))
      ?.map((user) => {
        const name = `${user?.firstName} ${user?.lastName}`;
        return { id: user?.cognitoId, value: `${name} (${user?.email})` };
      });
  }, [users]);

  const marketOptions = useMemo(() => {
    const options = (markets ?? [])?.map((m) => {
      return { id: m.market, value: m.name };
    });

    if (options.length > 1) {
      options.unshift(allMarketsOption);
    }

    return options;
  }, [markets]);

  const appOptions = useMemo(() => {
    return (
      apps?.map((app) => {
        return { id: app.name, value: app.name };
      }) || []
    );
  }, [apps]);

  const audienceOptions = [
    {
      id: audienceSetOptions.APP,
      value: t(strCode.APP_USERS_TITLE),
      Component: (
        <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={"100%"}>
          <DropdownInput
            id="apps-selector"
            title={t(strCode.SELECT_APPS_TITLE)}
            placeholder={t(strCode.SELECT_APPS_TITLE)}
            options={appOptions}
            selectedOptions={selectedApps}
            hasError={showErrors && missingFields?.selectedApps}
            onSelectOption={(id) => {
              setSelectedApps(
                dropdownUtil.updateSelectedOptions(id, selectedApps, appOptions, true)
              );
            }}
            isSearchable
            useChips
          />
        </WrapperContainer>
      )
    },
    {
      id: audienceSetOptions.DEVICE_TYPE,
      value: t(strCode.DEVICE_TYPE_USERS_TITLE),
      Component: (
        <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={"100%"}>
          <DropdownInput
            id="devices-selector"
            title={t(strCode.SELECT_DEVICES_TITLE)}
            placeholder={t(strCode.SELECT_DEVICES_TITLE)}
            options={deviceOptions}
            selectedOptions={selectedDevices}
            hasError={showErrors && missingFields?.selectedDevices}
            onSelectOption={(id) => {
              setSelectedDevices(
                dropdownUtil.updateSelectedOptions(id, selectedDevices, deviceOptions, true)
              );
            }}
            isSearchable
            useChips
          />
        </WrapperContainer>
      )
    },
    {
      id: audienceSetOptions.DUSN,
      value: t(strCode.DEVICE_SERIAL_NUMBER_TITLE),
      Component: (
        <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={"100%"}>
          <DropdownInput
            id="dusns-selector"
            title={t(strCode.SERIAL_NUMBERS_TITLE)}
            placeholder={t(strCode.SERIAL_NUMBERS_TITLE)}
            options={dusnOptions}
            selectedOptions={selectedDusns}
            hasError={showErrors && missingFields?.selectedDusns}
            onSelectOption={(id) => {
              setSelectedDusns(
                dropdownUtil.updateSelectedOptions(id, selectedDusns, dusnOptions, true)
              );
            }}
            isSearchable
            useChips
          />
        </WrapperContainer>
      )
    },
    {
      id: audienceSetOptions.USER,
      value: t(strCode.USER_TITLE),
      Component: (
        <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={"100%"}>
          <DropdownInput
            id="users-selector"
            title={t(strCode.USERS_TITLE)}
            placeholder={t(strCode.USERS_TITLE)}
            options={userOptions}
            selectedOptions={selectedUsers}
            hasError={showErrors && missingFields?.selectedUsers}
            onSelectOption={(id) => {
              setSelectedUsers(
                dropdownUtil.updateSelectedOptions(id, selectedUsers, userOptions, true)
              );
            }}
            isSearchable
            useChips
          />
        </WrapperContainer>
      )
    }
  ];

  const getAudienceSectionData = useCallback(() => {
    return {
      audienceSetOption,
      selectedMarkets,
      selectedApps,
      selectedDevices,
      selectedDusns,
      selectedUsers,
      versions
    };
  }, [
    audienceSetOption,
    selectedMarkets,
    selectedApps,
    selectedDevices,
    selectedDusns,
    selectedUsers,
    versions
  ]);

  const onSelectMarket = (id) => {
    // Not "all: option
    if (id) {
      let updatedMarkets = dropdownUtil.updateSelectedOptions(
        id,
        selectedMarkets,
        marketOptions,
        true
      );

      const isAllMarketsSelected =
        updatedMarkets.every((market) => market.id !== allMarketsOption.id) &&
        updatedMarkets.length === marketOptions.length - 1;
      const isAllOptionSelected = updatedMarkets.some(
        (market) => market.id === allMarketsOption.id
      );

      if (isAllMarketsSelected && !isAllOptionSelected) {
        updatedMarkets.push(allMarketsOption);
      }

      if (!isAllMarketsSelected && isAllOptionSelected) {
        updatedMarkets = updatedMarkets.filter((market) => market.id !== allMarketsOption.id);
      }

      setSelectedMarkets(updatedMarkets);
    } else {
      if (selectedMarkets.length === marketOptions.length) {
        setSelectedMarkets([]);
      } else {
        setSelectedMarkets(marketOptions);
      }
    }
  };

  const onDeleteSelectedMarket = (id) => {
    const updatedMarkets = dropdownUtil.updateSelectedOptions(
      id,
      selectedMarkets,
      marketOptions,
      true
    );

    setSelectedMarkets([...updatedMarkets.filter((market) => market.id !== allMarketsOption.id)]);
  };

  useEffect(() => {
    const isAppAudienceAndSelected =
      audienceSetOption === audienceSetOptions.APP ? !!selectedApps.length : true;
    const isDeviceAudienceAndSelected =
      audienceSetOption === audienceSetOptions.DEVICE_TYPE ? !!selectedDevices.length : true;
    const isDusnAudienceAndSelected =
      audienceSetOption === audienceSetOptions.DUSN ? !!selectedDusns.length : true;
    const isUserAudienceAndSelected =
      audienceSetOption === audienceSetOptions.USER ? !!selectedUsers.length : true;

    let isAudienceValid = true;

    switch (audienceSetOption) {
      case audienceSetOptions.APP:
        isAudienceValid = isAppAudienceAndSelected;
        break;

      case audienceSetOptions.DEVICE_TYPE:
        isAudienceValid = isDeviceAudienceAndSelected;
        break;

      case audienceSetOptions.DUSN:
        isAudienceValid = isDusnAudienceAndSelected;
        break;

      case audienceSetOptions.USER:
        isAudienceValid = isUserAudienceAndSelected;
        break;
    }

    const isVersionsValid = ![audienceSetOptions.DUSN, audienceSetOptions.USER].includes(
      audienceSetOption
    )
      ? versions.length > 0
      : true;

    const valid = isAudienceValid && isVersionsValid;

    const errorFields = {
      selectedApps:
        audienceSetOption === audienceSetOptions.APP ? !isAppAudienceAndSelected : false,
      selectedDevices:
        audienceSetOption === audienceSetOptions.DEVICE_TYPE ? !isDeviceAudienceAndSelected : false,
      selectedDusns:
        audienceSetOption === audienceSetOptions.DUSN ? !isDusnAudienceAndSelected : false,
      selectedUsers:
        audienceSetOption === audienceSetOptions.USER ? !isUserAudienceAndSelected : false,
      versions: !isVersionsValid
    };

    setMissingFields(errorFields);
    setIsValid(valid);
  }, [
    audienceSetOption,
    selectedApps,
    selectedDevices,
    selectedDusns,
    selectedUsers,
    versions.length
  ]);

  useEffect(() => {
    setShowErrors(false);

    setSelectedApps([]);
    setSelectedDevices([]);
    setSelectedDusns([]);
    setSelectedUsers([]);
  }, [audienceSetOption]);

  const resetFormData = () => {
    setAudienceSetOption(audienceSetOptions.APP);
    setSelectedMarkets([]);
    setSelectedApps([]);
    setSelectedDevices([]);
    setSelectedDusns([]);
    setSelectedUsers([]);
    setVersions([]);

    setIsValid(true);
    setShowErrors(false);
    setMissingFields(null);
  };

  const prepopulateData = () => {
    if (audienceData) {
      setAudienceSetOption(audienceData.audienceSetOption);
      setSelectedMarkets(
        audienceData.selectedMarkets.length > 0
          ? audienceData.selectedMarkets.length !== marketOptions.length
            ? [...audienceData.selectedMarkets]
            : [allMarketsOption, ...audienceData.selectedMarkets]
          : []
      );
      setSelectedApps(audienceData.selectedApps);
      setSelectedDevices(audienceData.selectedDevices);
      setSelectedDusns(audienceData.selectedDusns);
      setSelectedUsers(audienceData.selectedUsers);
      setVersions(audienceData.versions);
    }
  };

  useEffect(() => {
    prepopulateData();

    return () => {
      resetFormData();
    };
  }, [audienceData]);

  useImperativeHandle(ref, () => ({
    saveAudienceSectionData(validate = true) {
      if (!showErrors && validate) {
        setShowErrors(true);
      }

      if (!isValid && validate) {
        log.warn("AudienceSection: Unable to save audience section due to missing fields");

        notificationService.showError({
          title: t(strCode.MISSING_FIELDS),
          message: t(strCode.MISSING_FIELDS_MESSAGE)
        });

        return;
      }

      return getAudienceSectionData();
    }
  }));

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      gap={16}
      width={"100%"}>
      <SectionTitle>{t(strCode.AUDIENCE_TITLE)}</SectionTitle>

      <SectionWrapper>
        <DropdownInput
          id="markets-selector"
          title={t(strCode.SELECT_MARKETS_TITLE)}
          placeholder={t(strCode.SELECT_MARKETS_TITLE)}
          options={marketOptions}
          selectedOptions={selectedMarkets}
          warningMsg={t(strCode.OPTIONAL_INFO)}
          onSelectOption={onSelectMarket}
          InputComponent={
            selectedMarkets?.length > 0 && (
              <SelectedItemsList
                items={selectedMarkets}
                onDelete={onDeleteSelectedMarket}
                checkIsOptionDisabled={(id) => !marketOptions?.some((market) => market.id === id)}
              />
            )
          }
          showCheckboxes
        />
      </SectionWrapper>

      <RadioGroupSections
        title={t(strCode.SET_AUDIENCE_TITLE)}
        name="audience-set-options"
        options={audienceOptions}
        selectedId={audienceSetOption}
        onChange={setAudienceSetOption}
      />

      <CurrentFirmwareVersionInput
        versions={versions}
        setVersions={setVersions}
        hasError={showErrors && missingFields?.versions}
      />
    </WrapperContainer>
  );
});

AudienceSection.displayName = "AudienceSection";

AudienceSection.propTypes = {
  audienceData: PropTypes.shape({
    audienceSetOption: PropTypes.string.isRequired,
    selectedApps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    selectedDevices: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    selectedDusns: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    selectedUsers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    selectedMarkets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    versions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired
};

export default AudienceSection;
